import { hasAnalystReading } from '../noyau/analyst-prompt.js';
import { ANALYST_LEVELS, type AnalystLevel, type AnalystPass } from '../schemas/analyst.js';
import type { ProtocolAnalyst, ProtocolStepDraft } from '../schemas/protocol.js';
import { variablesOf } from './variables.js';

/**
 * WHAT THE PROTOCOL SAYS OF ITS OWN Analyste SECTION (analyste.md §3.5 to §3.7,
 * issue #196): whether it is complete, and whether the two readings cite an
 * objective the version no longer carries.
 *
 * Pure, and in `shared`, for the reason `problems.ts` gives: the editor draws the
 * yellow mark and the encart of an incomplete section, the boxes of the protocol
 * name the experiences it would stop, `check_protocol` warns, and the lookup of an
 * experience's gestures (`experienceProblems`) reads the same facts on the version
 * its pin resolves to - one rule, so none of them can say another thing.
 *
 * ## A section is complete when (§3.6, G21)
 *
 * - EACH of the three levels carries at least one objective, and
 * - BOTH readings are non-empty - spaces alone count as empty (N35).
 *
 * A protocol NEVER refuses because of it (K16): it warns, and the refusals belong
 * to the experiences that play it (`analyst-levels-missing`,
 * `analyst-reading-missing`).
 */

/** How many objectives carry each level - « Pourquoi : 2 objectifs · Comment : 2 · Quoi : 2 » (§3.5). */
export type AnalystLevelCounts = Readonly<Record<AnalystLevel, number>>;

/**
 * The LEVEL of every objective that carries one, in the order of the protocol -
 * the raw fact the lookup of an experience stores per version, and what the counts
 * and the gaps are read from.
 */
export function carriedAnalystLevels(steps: readonly ProtocolStepDraft[]): AnalystLevel[] {
  return steps.flatMap((step) =>
    step.objectives.flatMap((objective) =>
      objective.analystLevel === null ? [] : [objective.analystLevel],
    ),
  );
}

/** The counts of §3.5 over the carried levels. */
export function analystLevelCounts(carried: readonly AnalystLevel[]): AnalystLevelCounts {
  const count = (level: AnalystLevel): number => carried.filter((one) => one === level).length;
  return { why: count('why'), how: count('how'), what: count('what') };
}

/** What a section lacks to be complete. Both lists empty: it is complete. */
export type AnalystSectionGaps = {
  /** The levels no objective carries, in the order Pourquoi, Comment, Quoi. */
  readonly levelsMissing: readonly AnalystLevel[];
  /** The readings left empty - pass 1 then pass 2. */
  readonly readingsMissing: readonly AnalystPass[];
};

/**
 * The gaps of a section, from the levels the objectives carry and the two
 * readings (§3.6, G21): a level nobody carries, a reading that is empty after a
 * trim.
 */
export function analystSectionGapsOf(
  carried: readonly AnalystLevel[],
  readings: Pick<ProtocolAnalyst, 'functionsReading' | 'styleReading'>,
): AnalystSectionGaps {
  const counts = analystLevelCounts(carried);
  const readingsMissing: AnalystPass[] = [];
  if (!hasAnalystReading(readings.functionsReading)) readingsMissing.push(1);
  if (!hasAnalystReading(readings.styleReading)) readingsMissing.push(2);
  return {
    levelsMissing: ANALYST_LEVELS.filter((level) => counts[level] === 0),
    readingsMissing,
  };
}

/** The gaps of the section of a protocol's steps and readings - what the editor and `check_protocol` read. */
export function analystSectionGaps(
  steps: readonly ProtocolStepDraft[],
  analyst: Pick<ProtocolAnalyst, 'functionsReading' | 'styleReading'>,
): AnalystSectionGaps {
  return analystSectionGapsOf(carriedAnalystLevels(steps), analyst);
}

/** Whether a section is complete (§3.6): no level without an objective, no empty reading. */
export function analystSectionComplete(gaps: AnalystSectionGaps): boolean {
  return gaps.levelsMissing.length === 0 && gaps.readingsMissing.length === 0;
}

/**
 * Whether each reading cites an objective the steps do not carry (§3.3, V2): a
 * `{{objectif:id}}` whose id names nothing, because the objective was deleted or
 * the token came from another protocol. The analyst then reads « (objectif
 * retiré) » in its place and the protocol only WARNS, at the régime of
 * `protocolOrphans` - a criterion token is not judged here, a reading cites
 * objectives only (N48).
 */
export function analystReadingOrphans(
  steps: readonly ProtocolStepDraft[],
  analyst: Pick<ProtocolAnalyst, 'functionsReading' | 'styleReading'>,
): { readonly functionsReading: boolean; readonly styleReading: boolean } {
  const objectiveIds = new Set(steps.flatMap((step) => step.objectives.map((o) => o.id)));
  const citesGhost = (text: string): boolean =>
    variablesOf(text).some(
      (variable) => variable.kind === 'objective' && !objectiveIds.has(variable.id),
    );
  return {
    functionsReading: citesGhost(analyst.functionsReading),
    styleReading: citesGhost(analyst.styleReading),
  };
}

/**
 * The warnings a protocol gives about its Analyste section, never a refusal (K16):
 * an INCOMPLETE section - the levels no objective carries and the readings left
 * empty, once - then the readings that cite a ghost, one per pass. A protocol of
 * before #196 opens on the first: every objective without a level, both readings
 * empty.
 */
export type AnalystSectionWarning =
  | {
      readonly kind: 'analyst-section-incomplete';
      readonly levelsMissing: readonly AnalystLevel[];
      readonly readingsMissing: readonly AnalystPass[];
    }
  | { readonly kind: 'analyst-reading-orphan'; readonly pass: AnalystPass };

export function analystSectionWarnings(
  steps: readonly ProtocolStepDraft[],
  analyst: Pick<ProtocolAnalyst, 'functionsReading' | 'styleReading'>,
): AnalystSectionWarning[] {
  const warnings: AnalystSectionWarning[] = [];
  const gaps = analystSectionGaps(steps, analyst);
  if (!analystSectionComplete(gaps)) {
    warnings.push({ kind: 'analyst-section-incomplete', ...gaps });
  }
  const orphans = analystReadingOrphans(steps, analyst);
  if (orphans.functionsReading) warnings.push({ kind: 'analyst-reading-orphan', pass: 1 });
  if (orphans.styleReading) warnings.push({ kind: 'analyst-reading-orphan', pass: 2 });
  return warnings;
}
